import { supabase } from "@/lib/supabaseClient";

export type QuizOption = {
  key: string;
  label: string;
  sub: string;
  icon: string;
  archetype: string;
};

export type QuizQuestion = {
  id: string;
  order: number;
  text: string;
  options: QuizOption[];
};

export type PersonalityProfile = {
  key: string;
  label: string;
  temperament: string;
  description: string;
  strengths: string[];
  challenges: string[];
  icon: string;
};

export type QuizResult = {
  archetypeKey: string;
  answers: Record<string, string>;
  percent: number;
  totalQuestions: number;
  completedAt: string;
  profile: PersonalityProfile;
};

/** Las 16 preguntas del test, en orden, con sus 4 opciones cada una. */
export async function fetchQuizQuestions(): Promise<QuizQuestion[]> {
  const { data, error } = await supabase
    .from("quiz_questions")
    .select("id, question_order, text, options")
    .eq("active", true)
    .order("question_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    order: r.question_order as number,
    text: r.text as string,
    options: r.options as QuizOption[],
  }));
}

async function fetchProfileByKey(key: string): Promise<PersonalityProfile> {
  const { data, error } = await supabase
    .from("personality_profiles")
    .select("key, label, temperament, description, strengths, challenges, icon")
    .eq("key", key)
    .single();
  if (error) throw error;
  return {
    key: data.key as string,
    label: data.label as string,
    temperament: data.temperament as string,
    description: data.description as string,
    strengths: data.strengths as string[],
    challenges: data.challenges as string[],
    icon: data.icon as string,
  };
}

/** Resultado vigente del usuario autenticado, con el perfil de personalidad ya resuelto. null si aún no ha tomado el test. */
export async function fetchMyQuizResult(): Promise<QuizResult | null> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return null;

  const { data, error } = await supabase
    .from("quiz_results")
    .select("archetype_key, answers, percent, total_questions, completed_at")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const profile = await fetchProfileByKey(data.archetype_key as string);
  return {
    archetypeKey: data.archetype_key as string,
    answers: data.answers as Record<string, string>,
    percent: data.percent as number,
    totalQuestions: data.total_questions as number,
    completedAt: data.completed_at as string,
    profile,
  };
}

/**
 * Calcula el arquetipo ganador a partir de las respuestas (la letra que más
 * se repitió gana; en caso de empate gana la primera en el orden
 * inspirador > lider > analitico > observador, el mismo orden de las
 * opciones A-D en cada pregunta).
 */
function computeArchetype(answers: Record<string, QuizOption>): string {
  const tally: Record<string, number> = {};
  for (const opt of Object.values(answers)) {
    tally[opt.archetype] = (tally[opt.archetype] ?? 0) + 1;
  }
  const priority = ["inspirador", "lider", "analitico", "observador"];
  let winner = priority[0];
  let best = -1;
  for (const key of priority) {
    const count = tally[key] ?? 0;
    if (count > best) {
      best = count;
      winner = key;
    }
  }
  return winner;
}

/**
 * Guarda el resultado del test del usuario autenticado (upsert: si ya lo
 * había tomado antes, reemplaza el resultado vigente). Devuelve el
 * resultado ya resuelto con el perfil de personalidad.
 */
export async function submitQuizAnswers(
  answersByQuestionId: Record<string, QuizOption>
): Promise<QuizResult> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) throw new Error("Debes iniciar sesión para guardar tu resultado.");

  const archetypeKey = computeArchetype(answersByQuestionId);
  const totalQuestions = Object.keys(answersByQuestionId).length;
  const answersPlain: Record<string, string> = {};
  for (const [questionId, opt] of Object.entries(answersByQuestionId)) {
    answersPlain[questionId] = opt.key;
  }

  const { error } = await supabase.from("quiz_results").upsert(
    {
      user_id: userId,
      archetype_key: archetypeKey,
      answers: answersPlain,
      percent: 100,
      total_questions: totalQuestions,
      completed_at: new Date().toISOString(),
    },
    { onConflict: "user_id" }
  );
  if (error) throw error;

  const profile = await fetchProfileByKey(archetypeKey);
  return {
    archetypeKey,
    answers: answersPlain,
    percent: 100,
    totalQuestions,
    completedAt: new Date().toISOString(),
    profile,
  };
}
